import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  PublicationStatus,
  RoleAction,
  RolePermissionSubject,
  Shift,
} from '@wilson/interfaces';
import { CanAllShiftBeModifiedPipe } from '@wilson/non-domain-specific/shift-helpers/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzModalService } from 'ng-zorro-antd/modal';
import { firstValueFrom } from 'rxjs';
import { AbilityServiceSignal } from '@casl/angular';

@Component({
  selector: 'wilson-unassign-multi-select-shifts-button',
  imports: [
    NzButtonModule,
    TranslateModule,
    FeatureFlagsModule,
    CanAllShiftBeModifiedPipe,
    AuthorizationModule,
    NzTooltipModule,
  ],
  templateUrl: './unassign-multi-select-shifts-button.component.html',
  styleUrl: './unassign-multi-select-shifts-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnassignMultiSelectShiftsButtonComponent implements OnChanges {
  private readonly nzModalService = inject(NzModalService);
  private readonly translate = inject(TranslateService);

  @Input({ required: true }) selectedShifts!: (Shift & { id: string })[];
  @Input() isOperationOnGoing = false;
  @Output() unassignShiftsOperationTriggered = new EventEmitter();
  areShiftsAllUnassigned!: boolean;
  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedShifts']) {
      this.areShiftsAllUnassigned = this.selectedShifts.every(
        (shift) => shift.userId === null,
      );
    }
  }
  protected async updateShifts(): Promise<void> {
    const hasPublishedShift = this.selectedShifts.some(
      (shift) => shift.publicationStatus === PublicationStatus.Published,
    );
    if (hasPublishedShift) {
      if (await this.showShiftIsPublishedConfirmModal()) {
        this.unassignShiftsOperationTriggered.emit();
      }
    } else {
      this.unassignShiftsOperationTriggered.emit();
    }
  }

  private showShiftIsPublishedConfirmModal(): Promise<boolean> {
    const confirmModalRef = this.nzModalService.warning({
      nzTitle: this.translate.instant(
        'page.shift_timeline.drawer.shift_form.edit_shift',
      ),
      nzContent: this.translate.instant('shift.page.move_shifts_confirm'),
      nzCancelText: this.translate.instant('general.cancel'),
      nzOkText: this.translate.instant('general.ok'),
      nzOkType: 'primary',
      nzOkDanger: false,
      nzCentered: true,
      nzOnOk: () => {
        confirmModalRef.close(true);
      },
    });
    return firstValueFrom(confirmModalRef.afterClose);
  }
}
